import React from 'react';
import { LineChart, Line, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, Heart, Wind, Zap } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge-2";

// --- CUSTOM TOOLTIP ---
const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="rounded-lg border border-border/10
                   bg-popover/90 p-2 text-xs
                   shadow-md backdrop-blur-sm"
            >
                <p className="text-popover-foreground">{`Value: ${payload[0].value}`}</p>
            </div>
        );
    }
    return null;
};

// --- STAT CARD COMPONENT ---
function StatCard({ title, value, unit, icon: Icon, chartData, color, status }) {
    // Determine color based on value or standard
    const activeColor = color || '#3b82f6';

    // Create gradient ID unique to this card
    const gradientId = `gradient-${title.replace(/\s+/g, '-')}`;

    return (
        <Card className="overflow-hidden border-border/50 shadow-md transition-all hover:shadow-lg hover:border-border/80 group">
            <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
                    <div className="p-2 rounded-full bg-muted/50 group-hover:bg-muted transition-colors">
                        <Icon className="h-4 w-4 text-foreground/70" />
                    </div>
                </div>

                <div className="flex items-end justify-between gap-4">
                    <div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold tracking-tighter text-foreground">
                                {value ?? '--'}
                            </span>
                            <span className="text-sm text-muted-foreground font-medium">{unit}</span>
                        </div>
                        {status && (
                            <div className="mt-1">
                                <Badge variant="secondary" className="text-[10px] px-1.5 h-5">
                                    {status}
                                </Badge>
                            </div>
                        )}
                    </div>

                    <div className="h-12 w-28 shrink-0 pr-1">
                        {chartData && chartData.length > 1 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={activeColor} stopOpacity={0.3} />
                                            <stop offset="95%" stopColor={activeColor} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <Tooltip
                                        content={<CustomTooltip />}
                                        cursor={{
                                            stroke: 'rgba(255,255,255,0.1)',
                                            strokeWidth: 1,
                                            strokeDasharray: '3 3',
                                        }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke={activeColor}
                                        strokeWidth={2}
                                        dot={false}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground/30">
                                Watching...
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function ModernDashboard({ vitals, history, isScanning, sessionAverages }) {
    // history is expected to be { heart_rate: [], hrv_rmssd: [], breathing_rate: [] }

    // Format data for Recharts
    const hrData = history?.heart_rate?.map((v, i) => ({ i, value: v })) || [];
    const hrvData = history?.hrv_rmssd?.map((v, i) => ({ i, value: v })) || [];
    const brData = history?.breathing_rate?.map((v, i) => ({ i, value: v })) || [];

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
            <StatCard
                title="Heart Rate"
                value={sessionAverages?.heart_rate ? Math.round(sessionAverages.heart_rate) : (vitals?.heart_rate ? Math.round(vitals.heart_rate) : null)}
                unit="bpm"
                icon={Heart}
                chartData={hrData}
                color="#ef4444" // Red
                status={isScanning ? "Live" : "Final"}
            />

            <StatCard
                title="HRV (RMSSD)"
                value={sessionAverages?.hrv_rmssd ? Math.round(sessionAverages.hrv_rmssd) : (vitals?.hrv_rmssd ? Math.round(vitals.hrv_rmssd) : null)}
                unit="ms"
                icon={Activity}
                chartData={hrvData}
                color="#8b5cf6" // Violet
                status={isScanning ? "Live" : "Final"}
            />

            <StatCard
                title="Breathing Rate"
                value={sessionAverages?.breathing_rate ? Math.round(sessionAverages.breathing_rate) : (vitals?.breathing_rate ? Math.round(vitals.breathing_rate) : null)}
                icon={Wind}
                unit="rpm"
                chartData={brData}
                color="#0ea5e9" // Sky Blue
                status={isScanning ? "Live" : "Final"}
            />
        </div>
    );
}
